import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel } from "react-icons/fa";
import "../css/styles.css";

const WildHeartKenya = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348741/8_Days_Wild_Heart_Kenya_Safari_u0ichl.jpg"
            alt="Kenya Wild Heart Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Safari</span>
          <h1>8 Day Kenya Wild Heart Safari</h1>
          <p className="safari-detail-hero-sub">
            From Samburu's rugged north to Ol Pejeta's rhinos, Lake Nakuru's flamingos, and the legendary Masai Mara — feel Kenya's wild heartbeat.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>Kenya's Wild Heart</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            Kenya's heart beats wild and untamed, and this safari lets you feel it. From the rugged landscapes of Samburu in the north, to the rhino-rich grounds of Ol Pejeta, the bird-filled waters of Lake Nakuru, and the golden plains of the Masai Mara, every day brings a new adventure.
          </p>
          <p>
            This journey is for travelers who want to see the real Kenya, from its diverse wildlife to its unique cultures, without rushing. You'll experience lions basking in the sun, elephants wandering across open plains, and perhaps even rare northern species like Grevy's zebras and reticulated giraffes in Samburu.
          </p>
          <p>
            It's a safari that mixes excitement, discovery, and unforgettable landscapes — perfect for those who want more than the usual routes.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore the remote and scenic Samburu National Reserve</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>See northern Kenya's unique wildlife, including Grevy's zebras, Somali ostriches, and gerenuks</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Visit Ol Pejeta Conservancy, home to rhinos and a chimpanzee sanctuary</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Game drives in Lake Nakuru, famous for rhinos and flamingos</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Two full days of wildlife adventure in the legendary Masai Mara</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Cultural experiences with Maasai communities</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>8 Days / 7 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Samburu &ndash; Ol Pejeta &ndash; Lake Nakuru &ndash; Masai Mara</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Arrive in Nairobi and be warmly welcomed by your Nyatwende representative. Transfer to your hotel and relax after your journey or explore nearby attractions like the Giraffe Centre or David Sheldrick Elephant Orphanage.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Samburu National Reserve</h3>
              <p>
                After breakfast, depart Nairobi for the dry and rugged landscapes of Samburu in northern Kenya. The drive is scenic, passing through small towns, farmlands, and the Great Rift Valley.
              </p>
              <p>
                Arrive at your lodge in time for lunch, followed by an afternoon game drive. Samburu is home to many unique northern species, including Grevy's zebra, reticulated giraffe, and Somali ostrich.
              </p>
              <p className="safari-detail-day-note">Overnight in Samburu.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Samburu</h3>
              <p>
                Today is dedicated to exploring Samburu. Morning and afternoon game drives provide opportunities to see the unique wildlife of northern Kenya, as well as lions, elephants, and buffalo.
              </p>
              <p>
                Optional visit to a Samburu village gives insight into the traditional lifestyle and customs of the local communities.
              </p>
              <p className="safari-detail-day-note">Overnight in Samburu.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Samburu to Ol Pejeta Conservancy</h3>
              <p>
                After breakfast, travel south towards the beautiful Ol Pejeta Conservancy in Laikipia. Arrive in time for lunch and enjoy an afternoon game drive.
              </p>
              <p>
                Ol Pejeta is renowned for its rhinos, large elephant herds, and the chimpanzee sanctuary — the only one in Kenya.
              </p>
              <p className="safari-detail-day-note">Overnight at Sweetwaters Serena Camp.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Ol Pejeta to Lake Nakuru</h3>
              <p>
                Depart Ol Pejeta after breakfast and head to Lake Nakuru National Park in the Great Rift Valley. Arrive in time for lunch, followed by an afternoon game drive.
              </p>
              <p>
                The park is famous for rhinos, lions, giraffes, and hundreds of bird species, including pink flamingos when the lake is in season.
              </p>
              <p className="safari-detail-day-note">Overnight in Lake Nakuru.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Lake Nakuru to Masai Mara</h3>
              <p>
                After breakfast, journey to the world-famous Masai Mara National Reserve. Arrive in time for lunch at your lodge or camp, then head out on your first afternoon game drive in the Mara.
              </p>
              <p>
                The Mara's vast golden plains are home to lions, elephants, giraffes, and countless other animals.
              </p>
              <p className="safari-detail-day-note">Overnight in Masai Mara.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Full Day in Masai Mara</h3>
              <p>
                Spend a full day exploring the Mara. Morning and afternoon game drives offer opportunities to see the Big Five and capture breathtaking wildlife photography.
              </p>
              <p>
                Optional experiences include a Maasai village visit or a sunrise hot air balloon safari over the Mara plains.
              </p>
              <p className="safari-detail-day-note">Overnight in Masai Mara.</p>
            </div>
          </div>

          {/* Day 8 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">8</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 8 — Masai Mara to Nairobi</h3>
              <p>
                After breakfast, enjoy a last view of the Mara before beginning your drive back to Nairobi. Arrive in the afternoon where you will be dropped off at your hotel or the airport for your onward journey.
              </p>
              <p className="safari-detail-day-note">
                Your safari ends here, leaving you with unforgettable memories of Kenya's wildlife and landscapes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Nairobi */}
        <h3 className="safari-detail-accom-region-title">Nairobi</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">City Hotels</div>
            <ul>
              <li><FaHotel /> Tamarind Tree Hotel</li>
              <li><FaHotel /> Four Points by Sheraton Nairobi Airport</li>
            </ul>
          </div>
        </div>

        {/* Samburu */}
        <h3 className="safari-detail-accom-region-title">Samburu</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Samburu Simba Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Samburu Intrepids</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ashnil Samburu</li>
              <li><FaHotel /> Elephant Bedroom Camp</li>
            </ul>
          </div>
        </div>

        {/* Ol Pejeta */}
        <h3 className="safari-detail-accom-region-title">Ol Pejeta Conservancy</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Camp</div>
            <ul>
              <li><FaHotel /> Sweetwaters Serena Camp</li>
            </ul>
          </div>
        </div>

        {/* Lake Nakuru */}
        <h3 className="safari-detail-accom-region-title">Lake Nakuru</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Lake Nakuru Sopa Lodge</li>
              <li><FaHotel /> Sarova Lion Hill Game Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> The Cliff Nakuru</li>
            </ul>
          </div>
        </div>

        {/* Masai Mara */}
        <h3 className="safari-detail-accom-region-title">Masai Mara</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Enkorok Mara Camp</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Zebra Plains Mara Camp</li>
              <li><FaHotel /> Mara Sopa Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Mara Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Nairobi</li>
              <li>Transport in a comfortable safari vehicle with a professional guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as Maasai village visits or hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can be tailored to suit your travel style. You may upgrade lodges, extend your stay, or combine it with a relaxing beach holiday on Kenya's coast.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=8 Day Kenya Wild Heart Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WildHeartKenya;
